/**
 * 远期房态图表数据转换
 * 基于基础数据（来自后端 API 或前端直调 getAliasPriceQuantity）按对比类型生成图表所需的 series / dataset
 */

import type { FsmFutureRoomStatusItem } from '../../../../../service/storeRevenueApi';
import type { AliasPriceQuantityResponse } from '../../../utils/priceChannelDataTransformer';
import { AXIS_LABEL_INTEGER } from '../../../configs/tooltipCommon';
import { COMPARISON_REFERENCE } from '@ms/shared-components';

export interface RoomStatusRecentlyChartDataResult {
  categories: string[] | undefined;
  dataset: Record<string, unknown>;
  axes: unknown;
  series: Array<Record<string, unknown>>;
}

type ComparisonField =
  | number[]
  | { value: number[]; color?: string; label?: string }
  | undefined;

function extractComparisonArray(
  raw: ComparisonField,
  fallbackLabel: string,
  defaultColor: string
): { value: number[]; color: string; name: string } {
  if (Array.isArray(raw)) {
    return { value: raw, color: defaultColor, name: fallbackLabel };
  }
  if (raw && typeof raw === "object" && "value" in raw) {
    const o = raw as { value: number[]; color?: string; label?: string };
    return {
      value: o.value,
      color: o.color || defaultColor,
      name: o.label || fallbackLabel,
    };
  }
  return { value: [], color: defaultColor, name: fallbackLabel };
}

/**
 * 生成远期房态图表数据：年同比、周环比两条辅助线同时输出，由 ECharts 图例控制显隐
 */
export const createRoomStatusRecentlyChartData = (
  baseData: {
    categories?: string[];
    dataset?: Record<string, unknown>;
    axes?: unknown;
    baseSeries?: Array<{ id?: string; type?: string; dataKey?: string; stack?: string; stackId?: string;[k: string]: unknown }>;
    series?: Array<{ id?: string; type?: string; dataKey?: string; stack?: string; stackId?: string;[k: string]: unknown }>;
    comparison?: {
      yearOverYear?: number[] | { value: number[]; color?: string; label?: string };
      weekOverWeek?: number[] | { value: number[]; color?: string; label?: string };
    };
  }
): RoomStatusRecentlyChartDataResult => {
  const yoy = extractComparisonArray(
    baseData.comparison?.yearOverYear,
    COMPARISON_REFERENCE.yearOverYear.label,
    COMPARISON_REFERENCE.yearOverYear.color
  );
  const wow = extractComparisonArray(
    baseData.comparison?.weekOverWeek,
    COMPARISON_REFERENCE.weekOverWeek.label,
    COMPARISON_REFERENCE.weekOverWeek.color
  );

  const baseSeries = baseData.baseSeries || baseData.series || [];

  if (process.env.NODE_ENV === "development") {
    const klineSeries = baseSeries.find(
      (s: { type?: string; id?: string }) =>
        s.type === "candlestick" || s.id === "kline"
    );
    if (klineSeries) {
      console.log("[createRoomStatusRecentlyChartData] K线图系列找到", {
        id: klineSeries.id,
        type: klineSeries.type,
        dataKey: klineSeries.dataKey,
        hasData: !!baseData.dataset?.[klineSeries.dataKey as string],
      });
    } else {
      console.warn(
        "[createRoomStatusRecentlyChartData] K线图系列未在baseSeries中找到",
        {
          baseSeriesTypes: baseSeries.map((s: { id?: string; type?: string; dataKey?: string }) => ({
            id: s.id,
            type: s.type,
            dataKey: s.dataKey,
          })),
        }
      );
    }
  }

  const mappedSeries = baseSeries.map(
    (s: { dataKey?: string; stack?: string; stackId?: string; id?: string;[k: string]: unknown }, i: number) => {
      let dataKey = s.dataKey;
      if (dataKey && String(dataKey).startsWith("remainingRooms.")) {
        const roomType = String(dataKey).replace("remainingRooms.", "");
        dataKey = `remaining_${roomType}`;
      }
      if (dataKey && String(dataKey).startsWith("occupiedRooms.")) {
        const roomType = String(dataKey).replace("occupiedRooms.", "");
        dataKey = `occupied_${roomType}`;
      }
      return {
        ...s,
        id: s.id ?? `series-${i}`,
        stack: s.stack || s.stackId,
        dataKey,
        showInLegend: false,
      } as Record<string, unknown>;
    }
  );

  const baseDataset = baseData.dataset ? { ...baseData.dataset } : {};
  const result: RoomStatusRecentlyChartDataResult = {
    categories: baseData.categories,
    dataset: {
      ...baseDataset,
      comparisonYearOverYear: yoy.value,
      comparisonWeekOverWeek: wow.value,
    },
    axes: baseData.axes,
    series: [
      ...mappedSeries,
      {
        id: "comparison-yoy",
        name: yoy.name,
        dataKey: "comparisonYearOverYear",
        type: "line",
        axisId: "right",
        color: COMPARISON_REFERENCE.yearOverYear.color,
        showInLegend: true,
        lineStyle: { ...COMPARISON_REFERENCE.yearOverYear.lineStyle },
      },
      {
        id: "comparison-wow",
        name: wow.name,
        dataKey: "comparisonWeekOverWeek",
        type: "line",
        axisId: "right",
        color: COMPARISON_REFERENCE.weekOverWeek.color,
        showInLegend: true,
        lineStyle: { ...COMPARISON_REFERENCE.weekOverWeek.lineStyle },
      },
    ],
  };
  return result;
};

const EMPTY_BASE_CHART_DATA = {
  categories: [] as string[],
  dataset: {
    stayingRooms: [] as number[],
    remainingRooms: {} as Record<string, number[]>,
    totalRooms: [] as number[],
    klineData: [] as number[][],
    averagePrice: [] as number[],
    highestPrice: [] as number[],
    lowestPrice: [] as number[],
    yearOverYear: [] as number[],
    weekOverWeek: [] as number[],
  },
  axes: {
    left: { id: 'left', position: 'left' as const, name: '房间数', unit: '间', min: 0, max: 500 },
    right: { id: 'right', position: 'right' as const, name: '价格', unit: '元', min: 0, max: 600 },
  },
  baseSeries: [] as Array<{ id: string; name: string; dataKey: string; type: 'bar' | 'candlestick'; axisId: string; stackId?: string }>,
  comparison: {
    yearOverYear: {
      value: [] as number[],
      color: COMPARISON_REFERENCE.yearOverYear.color,
      strokeDasharray: COMPARISON_REFERENCE.yearOverYear.strokeDasharray,
      label: COMPARISON_REFERENCE.yearOverYear.label,
    },
    weekOverWeek: {
      value: [] as number[],
      color: COMPARISON_REFERENCE.weekOverWeek.color,
      strokeDasharray: COMPARISON_REFERENCE.weekOverWeek.strokeDasharray,
      label: COMPARISON_REFERENCE.weekOverWeek.label,
    },
  },
};

/**
 * 将 getAliasPriceQuantity 接口返回转为远期房态组件所需的 baseChartData
 */
export function aliasPriceQuantityToBaseChartData(
  response: AliasPriceQuantityResponse
): typeof EMPTY_BASE_CHART_DATA {
  if (!response.data || response.data.length === 0) {
    return { ...EMPTY_BASE_CHART_DATA };
  }
  const data = response.data;
  const categories = data.map((d) => {
    const date = new Date(d.date * 1000);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  });
  const allAliasNames = Array.from(
    new Set(data.flatMap((day) => day.aliases.map((a) => a.aliasName)))
  );
  const remainingRooms: Record<string, number[]> = {};
  allAliasNames.forEach((name) => {
    remainingRooms[name] = data.map((day) => {
      const a = day.aliases.find((x) => x.aliasName === name);
      return a?.quantityOpen ?? 0;
    });
  });
  const stayingRooms = data.map((day) => {
    return day.aliases.reduce((sum, a) => {
      const total = a.totalQuantity ?? 0;
      return sum + (total - (a.quantityOpen ?? 0));
    }, 0);
  });
  const totalRooms = data.map((day) =>
    day.aliases.reduce((sum, a) => sum + (a.totalQuantity ?? 0), 0)
  );
  const dayPrices = data.map((day) => {
    const prices = day.aliases.map((a) => a.currentPrice ?? 0).filter((p) => p > 0);
    const fromAliases = prices.length ? { min: Math.min(...prices), max: Math.max(...prices) } : { min: 0, max: 0 };
    const min = day.priceMin != null && Number.isFinite(day.priceMin) ? day.priceMin : fromAliases.min;
    const max = day.priceMax != null && Number.isFinite(day.priceMax) ? day.priceMax : fromAliases.max;
    return { min, max };
  });
  const klineData = dayPrices.map(({ min, max }) => [
    min,
    max,
    min,
    max,
  ]);
  const averagePrice = data.map((day) => {
    if (!day.aliases.length) return 0;
    const sum = day.aliases.reduce((s, a) => s + (a.currentPrice ?? 0), 0);
    return Math.round(sum / day.aliases.length);
  });
  const highestPrice = dayPrices.map((p) => p.max);
  const lowestPrice = dayPrices.map((p) => p.min);
  const baseSeries: Array<{ id: string; name: string; dataKey: string; type: 'bar' | 'candlestick' | 'line'; axisId: string; stackId?: string }> = [
    { id: 'staying', name: '已占用', dataKey: 'stayingRooms', type: 'bar', axisId: 'left', stackId: 'room' },
    ...allAliasNames.map((name) => ({
      id: `remaining_${name}`,
      name: `${name}(可售)`,
      dataKey: `remainingRooms.${name}`,
      type: 'bar' as const,
      axisId: 'left',
      stackId: 'room',
    })),
    { id: 'totalRooms', name: '总房量', dataKey: 'totalRooms', type: 'line', axisId: 'left' },
    { id: 'kline', name: '价格', dataKey: 'klineData', type: 'candlestick', axisId: 'right' },
  ];
  const maxPrice = Math.max(...dayPrices.map((p) => p.max), 1);
  const maxTotalRooms = Math.max(...totalRooms, 1);
  const leftAxisMax = Math.ceil(maxTotalRooms * 2);
  const rightAxisMax = maxPrice > 600 ? Math.ceil(maxPrice * 1.05) : 600;
  return {
    categories,
    dataset: {
      stayingRooms,
      remainingRooms,
      totalRooms,
      klineData,
      averagePrice,
      highestPrice,
      lowestPrice,
      yearOverYear: [],
      weekOverWeek: [],
    },
    axes: {
      left: { id: 'left', position: 'left', name: '房间数', unit: '间', min: 0, max: leftAxisMax },
      right: { id: 'right', position: 'right', name: '价格', unit: '元', min: 0, max: rightAxisMax },
    },
    baseSeries,
    comparison: {
      yearOverYear: {
        value: [],
        color: COMPARISON_REFERENCE.yearOverYear.color,
        strokeDasharray: COMPARISON_REFERENCE.yearOverYear.strokeDasharray,
        label: COMPARISON_REFERENCE.yearOverYear.label,
      },
      weekOverWeek: {
        value: [],
        color: COMPARISON_REFERENCE.weekOverWeek.color,
        strokeDasharray: COMPARISON_REFERENCE.weekOverWeek.strokeDasharray,
        label: COMPARISON_REFERENCE.weekOverWeek.label,
      },
    },
  };
}

/**
 * 将 FSM futureRoomStatusList 转为与 aliasPriceQuantity 一致的 baseChartData
 */
export function buildBaseChartDataFromFutureRoomStatusList(
  list: FsmFutureRoomStatusItem[]
): typeof EMPTY_BASE_CHART_DATA {
  if (!list?.length) return { ...EMPTY_BASE_CHART_DATA };

  const hasDayRowsWithVo = list.some(
    (r) =>
      r.date != null &&
      Array.isArray(r.futureRoomStatusVoList) &&
      r.futureRoomStatusVoList.length > 0
  );
  let sorted: FsmFutureRoomStatusItem[];
  let totalRooms: number[];
  let stayingRooms: number[];
  let allAliasNames: string[];
  let remainingRooms: Record<string, number[]>;
  let occupiedRooms: Record<string, number[]>;

  if (hasDayRowsWithVo) {
    const dayRows = list.filter(
      (row) =>
        row.date != null &&
        Array.isArray(row.futureRoomStatusVoList) &&
        row.futureRoomStatusVoList.length > 0
    );
    sorted = [...dayRows].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    totalRooms = sorted.map((d) => d.allCount ?? 0);
    stayingRooms = sorted.map((d) => (d.allCount ?? 0) - (d.canSellCount ?? 0));
    allAliasNames = Array.from(
      new Set(
        sorted.flatMap((d) =>
          (d.futureRoomStatusVoList ?? [])
            .map((v) => v.aliasName)
            .filter((x): x is string => x != null && x !== '')
        )
      )
    );
    remainingRooms = {};
    occupiedRooms = {};
    allAliasNames.forEach((name) => {
      remainingRooms[name] = sorted.map((day) => {
        const vo = (day.futureRoomStatusVoList ?? []).find((v) => v.aliasName === name);
        return vo?.canSellCount ?? 0;
      });
      occupiedRooms[name] = sorted.map((day) => {
        const vo = (day.futureRoomStatusVoList ?? []).find((v) => v.aliasName === name);
        const all = vo?.allCount ?? 0;
        const can = vo?.canSellCount ?? 0;
        return all - can;
      });
    });
  } else {
    const byDate = new Map<string, FsmFutureRoomStatusItem[]>();
    list.forEach((row) => {
      if (row.date == null) return;
      if (!byDate.has(row.date)) byDate.set(row.date, []);
      byDate.get(row.date)!.push(row);
    });
    const dates = Array.from(byDate.keys()).sort();
    if (dates.length === 0) return { ...EMPTY_BASE_CHART_DATA };
    sorted = dates.map((date) => {
      const rows = byDate.get(date)!;
      const first = rows[0];
      const total = rows.reduce((s, r) => s + (r.allCount ?? 0), 0);
      const canSell = rows.reduce((s, r) => s + (r.canSellCount ?? 0), 0);
      return { ...first, allCount: total, canSellCount: canSell } as FsmFutureRoomStatusItem;
    });
    totalRooms = sorted.map((d) => d.allCount ?? 0);
    stayingRooms = sorted.map((d) => (d.allCount ?? 0) - (d.canSellCount ?? 0));
    allAliasNames = Array.from(
      new Set(
        list.map((r) => r.aliasName).filter((x): x is string => x != null && x !== '')
      )
    );
    remainingRooms = {};
    occupiedRooms = {};
    allAliasNames.forEach((name) => {
      remainingRooms[name] = dates.map((date) => {
        const row = byDate.get(date)?.find((r) => r.aliasName === name);
        return row?.canSellCount ?? 0;
      });
      occupiedRooms[name] = dates.map((date) => {
        const row = byDate.get(date)?.find((r) => r.aliasName === name);
        const all = row?.allCount ?? 0;
        const can = row?.canSellCount ?? 0;
        return all - can;
      });
    });
  }

  const categories = sorted.map((d) => {
    const parts = (d.date || '').split('-').map(Number);
    if (parts.length >= 2) return `${parts[1]}/${parts[2] ?? 0}`;
    return d.date || '';
  });
  const n = sorted.length;
  const klineData = Array(n).fill([0, 0, 0, 0]);
  const averagePrice = Array(n).fill(0);
  const highestPrice = Array(n).fill(0);
  const lowestPrice = Array(n).fill(0);
  const maxTotal = Math.max(...totalRooms, 1);
  const leftAxisMax = Math.ceil(maxTotal * 2);
  const baseSeries: Array<{ id: string; name: string; dataKey: string; type: 'bar' | 'candlestick' | 'line'; axisId: string; stackId?: string }> = [
    ...(allAliasNames.length > 0
      ? [
        ...allAliasNames.map((name) => ({
          id: `occupied_${name}`,
          name: `${name}(已占用)`,
          dataKey: `occupiedRooms.${name}`,
          type: 'bar' as const,
          axisId: 'left',
          stackId: 'room',
        })),
        ...allAliasNames.map((name) => ({
          id: `remaining_${name}`,
          name: `${name}(可售)`,
          dataKey: `remainingRooms.${name}`,
          type: 'bar' as const,
          axisId: 'left',
          stackId: 'room',
        })),
      ]
      : [{ id: 'staying', name: '已占用', dataKey: 'stayingRooms', type: 'bar' as const, axisId: 'left', stackId: 'room' }]),
    { id: 'totalRooms', name: '总房量', dataKey: 'totalRooms', type: 'line', axisId: 'left' },
    { id: 'kline', name: '价格', dataKey: 'klineData', type: 'candlestick', axisId: 'right' },
  ];
  return {
    categories,
    dataset: {
      stayingRooms,
      remainingRooms,
      occupiedRooms,
      totalRooms,
      klineData,
      averagePrice,
      highestPrice,
      lowestPrice,
      yearOverYear: [],
      weekOverWeek: [],
    },
    axes: {
      left: { id: 'left', position: 'left', name: '房间数', unit: '间', min: 0, max: leftAxisMax },
      right: { id: 'right', position: 'right', name: '价格', unit: '元', min: 0, max: 600 },
    },
    baseSeries,
    comparison: {
      yearOverYear: {
        value: [],
        color: COMPARISON_REFERENCE.yearOverYear.color,
        strokeDasharray: COMPARISON_REFERENCE.yearOverYear.strokeDasharray,
        label: COMPARISON_REFERENCE.yearOverYear.label,
      },
      weekOverWeek: {
        value: [],
        color: COMPARISON_REFERENCE.weekOverWeek.color,
        strokeDasharray: COMPARISON_REFERENCE.weekOverWeek.strokeDasharray,
        label: COMPARISON_REFERENCE.weekOverWeek.label,
      },
    },
  };
}

const STACK_COLORS = ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4'];

/**
 * 从 FSM futureRoomStatusList 构建各房型累计图（堆叠柱状）
 */
export function buildRoomTypeStackedChartFromFutureRoomStatusList(
  list: FsmFutureRoomStatusItem[],
  mode: 'canSell' | 'occupied'
): RoomStatusRecentlyChartDataResult | null {
  if (!list?.length) return null;

  const hasDayRowsWithVo = list.some(
    (r) =>
      r.date != null &&
      Array.isArray(r.futureRoomStatusVoList) &&
      r.futureRoomStatusVoList.length > 0
  );

  let categories: string[];
  let allAliasNames: string[];
  let dataset: Record<string, number[]>;

  if (hasDayRowsWithVo) {
    const dayRows = list.filter(
      (row) =>
        row.date != null &&
        Array.isArray(row.futureRoomStatusVoList) &&
        row.futureRoomStatusVoList.length > 0
    );
    const sorted = [...dayRows].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    categories = sorted.map((d) => {
      const parts = (d.date || '').split('-').map(Number);
      if (parts.length >= 2) return `${parts[1]}/${parts[2] ?? 0}`;
      return d.date || '';
    });
    allAliasNames = Array.from(
      new Set(
        sorted.flatMap((d) =>
          (d.futureRoomStatusVoList ?? [])
            .map((v) => v.aliasName)
            .filter((x): x is string => x != null && x !== '')
        )
      )
    );
    dataset = {};
    allAliasNames.forEach((aliasName) => {
      dataset[aliasName] = sorted.map((day) => {
        const vo = (day.futureRoomStatusVoList ?? []).find((v) => v.aliasName === aliasName);
        if (!vo) return 0;
        if (mode === 'canSell') return vo.canSellCount ?? 0;
        return (vo.allCount ?? 0) - (vo.canSellCount ?? 0);
      });
    });
  } else {
    const byDateAll = new Map<string, FsmFutureRoomStatusItem[]>();
    list.forEach((row) => {
      if (row.date == null) return;
      if (!byDateAll.has(row.date)) byDateAll.set(row.date, []);
      byDateAll.get(row.date)!.push(row);
    });
    const dates = Array.from(byDateAll.keys()).sort((a, b) => a.localeCompare(b));
    if (dates.length === 0) return null;
    categories = dates.map((date) => {
      const parts = date.split('-').map(Number);
      if (parts.length >= 2) return `${parts[1]}/${parts[2] ?? 0}`;
      return date;
    });
    const flatAliasNames = Array.from(
      new Set(list.map((r) => r.aliasName).filter((x): x is string => x != null && x !== ''))
    );
    if (flatAliasNames.length > 0) {
      allAliasNames = flatAliasNames;
      dataset = {};
      allAliasNames.forEach((aliasName) => {
        dataset[aliasName] = dates.map((date) => {
          const row = byDateAll.get(date)?.find((r) => r.aliasName === aliasName);
          if (!row) return 0;
          if (mode === 'canSell') return row.canSellCount ?? 0;
          return (row.allCount ?? 0) - (row.canSellCount ?? 0);
        });
      });
    } else {
      allAliasNames = ['全部'];
      dataset = {
        全部: dates.map((date) => {
          const rows = byDateAll.get(date) ?? [];
          const first = rows[0];
          if (!first) return 0;
          if (mode === 'canSell') return first.canSellCount ?? 0;
          return (first.allCount ?? 0) - (first.canSellCount ?? 0);
        }),
      };
    }
  }

  const maxVal = Math.max(
    ...allAliasNames.flatMap((name) => dataset[name] ?? []),
    1
  );
  const leftMax = Math.ceil(maxVal * 1.2);
  const series = allAliasNames.map((name, i) => ({
    id: `room_${name}`,
    name,
    dataKey: name,
    type: 'bar' as const,
    axisId: 'left',
    stackId: 'byRoomType',
    color: STACK_COLORS[i % STACK_COLORS.length],
  }));
  return {
    categories,
    dataset,
    axes: {
      left: {
        id: 'left',
        position: 'left',
        name: mode === 'canSell' ? '可售房量' : '已占用房量',
        unit: '间',
        min: 0,
        max: leftMax,
        axisLabel: AXIS_LABEL_INTEGER,
      },
    },
    series,
  };
}
